import {
  ReactNode,
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  HTTP_STATUSES,
  MODAL_TITLES,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IApiResponse } from "@miplatform/types";
import {
  ConfirmModalConfig,
  CustomerPricingDetailsContextType,
  TabFormState,
} from "./CustomerPricingDetails.types";
import { useCustomerPricingDetailsViewModel } from "./useCustomerPricingDetailsViewModel";

const CustomerPricingDetailsContext =
  createContext<CustomerPricingDetailsContextType | null>(null);

export function CustomerPricingDetailsProvider({
  children,
}: {
  children: ReactNode;
}) {
  // account details
  const { params, goBack } = useRouter();
  const { id: accountId } = params;

  const {
    isLoading: isViewModelLoading,
    accountDetails,
    getAccountDetails,
  } = useCustomerPricingDetailsViewModel();

  useEffect(() => {
    if (accountId) {
      getAccountDetails(accountId);
    }
  }, [accountId]);

  // tab form state management and save handling
  const [tabFormState, setTabFormState] = useState<TabFormState>(
    DEFAULT_TAB_FORM_STATE
  );

  const saveHandlerRef = useRef<(() => Promise<IApiResponse | void>) | null>(
    null
  );
  const cancelHandlerRef = useRef<(() => void) | null>(() => goBack());

  // confirm modal
  const [confirmModalConfig, setConfirmModalConfig] =
    useState<ConfirmModalConfig | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const setSaveHandler = useCallback(
    (handler: (() => Promise<IApiResponse | void>) | null) => {
      saveHandlerRef.current = handler;
    },
    []
  );

  const setCancelHandler = useCallback((handler: (() => void) | null) => {
    cancelHandlerRef.current = handler;
  }, []);

  // footer cancel/save action handlers
  const handleSave = useCallback(async () => {
    if (!saveHandlerRef.current) return;

    if (confirmModalConfig) {
      setIsConfirmModalOpen(true);
    } else {
      const response = await saveHandlerRef.current();
      if (
        (response?.status === HTTP_STATUSES.OK ||
          response?.status === HTTP_STATUSES.NO_CONTENT) &&
        accountId
      ) {
        getAccountDetails(accountId);
      }
    }
  }, [accountId, confirmModalConfig]);

  const handleCancel = useCallback(() => {
    if (cancelHandlerRef.current) {
      cancelHandlerRef.current();
    }
  }, []);

  // modal handler functions
  const handleConfirmModalSave = useCallback(async () => {
    if (!saveHandlerRef.current) return;

    setIsConfirmLoading(true);
    const response = await saveHandlerRef.current();
    setIsConfirmLoading(false);
    setIsConfirmModalOpen(false);

    if (
      (response?.status === HTTP_STATUSES.OK ||
        response?.status === HTTP_STATUSES.NO_CONTENT) &&
      accountId
    ) {
      getAccountDetails(accountId);
    }
  }, [accountId]);

  const handleConfirmModalCancel = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, []);

  const handleConfirmModalSecondaryPositive = useCallback(async () => {
    if (!confirmModalConfig?.handleSecondaryPositive) return;

    setIsConfirmLoading(true);
    const response = await confirmModalConfig.handleSecondaryPositive();
    setIsConfirmLoading(false);
    setIsConfirmModalOpen(false);

    if (
      (response?.status === HTTP_STATUSES.OK ||
        response?.status === HTTP_STATUSES.NO_CONTENT) &&
      accountId
    ) {
      getAccountDetails(accountId);
    }
  }, [accountId, confirmModalConfig]);

  const value = useMemo(
    () => ({
      accountDetails,
      getAccountDetails,
      isViewModelLoading,
      tabFormState,
      setTabFormState,
      setSaveHandler,
      setConfirmModalConfig,
      handleSave,
      setCancelHandler,
      handleCancel,
    }),
    [
      accountDetails,
      getAccountDetails,
      isViewModelLoading,
      tabFormState,
      setTabFormState,
      setSaveHandler,
      setConfirmModalConfig,
      handleSave,
      setCancelHandler,
      handleCancel,
    ]
  );

  return (
    <CustomerPricingDetailsContext value={value}>
      {children}
      {confirmModalConfig && (
        <MiModalConfirm
          open={isConfirmModalOpen}
          loading={isConfirmLoading}
          title={confirmModalConfig.title || MODAL_TITLES.SAVE}
          description={confirmModalConfig.description}
          textPositive={confirmModalConfig.textPositive || BUTTON_LABELS.SAVE}
          textSecondaryPositive={confirmModalConfig.textSecondaryPositive}
          buttonPositiveColor={
            confirmModalConfig.buttonPositiveColor || BUTTON_COLORS.PRIMARY
          }
          handlePositive={handleConfirmModalSave}
          handleNegative={handleConfirmModalCancel}
          handleSecondaryPositive={
            confirmModalConfig.handleSecondaryPositive
              ? handleConfirmModalSecondaryPositive
              : undefined
          }
          hideNegative={confirmModalConfig.hideNegative ?? false}
        />
      )}
    </CustomerPricingDetailsContext>
  );
}

export function useCustomerPricingDetailsContext() {
  const context = use(CustomerPricingDetailsContext);
  if (!context) {
    throw new Error(
      "useCustomerPricingDetailsContext must be used within a CustomerPricingDetailsProvider"
    );
  }
  return context;
}

export function useTabFormRegistration<T extends FieldValues>(
  form: UseFormReturn<T>,
  isTabLoading = false
) {
  const {
    setTabFormState,
    setSaveHandler,
    setCancelHandler,
    setConfirmModalConfig,
  } = useCustomerPricingDetailsContext();
  const { goBack } = useRouter();
  const {
    formState: { isDirty, isValid },
  } = form;

  // register tab's formState variables to context
  useEffect(() => {
    setTabFormState({ isDirty, isValid, isLoading: isTabLoading });
  }, [isDirty, isValid, isTabLoading]);

  useEffect(() => {
    // cleanup on unmount
    return () => {
      setTabFormState(DEFAULT_TAB_FORM_STATE);
      setSaveHandler(null);
      setConfirmModalConfig(null);
      setCancelHandler(() => goBack());
    };
  }, [
    setTabFormState,
    setSaveHandler,
    setCancelHandler,
    setConfirmModalConfig,
  ]);
}

const DEFAULT_TAB_FORM_STATE: TabFormState = {
  isDirty: false,
  isValid: false,
  isLoading: false,
};
